'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

export default function HeroSection() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
    // Trigger entrance animation
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  if (!isHydrated) {
    return (
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-primary via-navy-dark to-charcoal overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(212,175,55,0.1),transparent_50%)]" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-32 text-center">
          <div className="space-y-8">
            <div className="h-24 w-3/4 bg-white/10 rounded mx-auto animate-pulse" />
            <div className="h-6 w-1/2 bg-white/10 rounded mx-auto animate-pulse" />
            <div className="h-14 w-64 bg-accent/20 rounded mx-auto animate-pulse" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Sophisticated Overlay */}
      <div className="absolute inset-0">
        <AppImage
          src="/assets/images/homepage_hero_legal_background.png"
          alt="Prestigious law firm office with elegant cityscape backdrop"
          className="w-full h-full object-cover"
          priority={true}
        />
        {/* Layered gradient for depth with specified opacity */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/50 to-black/55" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black/30" />
      </div>

      {/* Subtle animated overlay pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(212,175,55,0.3),transparent_70%)] animate-pulse" style={{ animationDuration: '4s' }} />
      </div>

      {/* Main Content - Animated Entrance */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-32">
        <div className={`text-center transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          
          {/* Firm Name - Grand Entrance Style */}
          <div className="space-y-8 mb-16">
            <div className="space-y-2">
              {/* Main firm name with elegant spacing - White with text shadow and stroke */}
              <h1 
                className="font-serif text-7xl md:text-8xl lg:text-9xl font-bold leading-none tracking-wider"
                style={{
                  color: '#FFFFFF',
                  textShadow: '0 4px 12px rgba(0,0,0,0.55)',
                  WebkitTextStroke: '1.5px rgba(0,0,0,0.5)',
                  paintOrder: 'stroke fill'
                }}
              >
                LUBBERT
              </h1>
              
              {/* Associates with refined styling - Gold with text shadow */}
              <h2 
                className="font-serif text-4xl md:text-5xl lg:text-6xl font-light tracking-[0.3em]"
                style={{
                  color: '#D4AF37',
                  textShadow: '0 3px 8px rgba(0,0,0,0.45)'
                }}
              >
                ASSOCIATES
              </h2>
            </div>
            
            {/* Decorative divider with animation */}
            <div className="flex items-center justify-center space-x-4">
              <div className={`h-px bg-accent transition-all duration-1000 delay-300 ${isVisible ? 'w-16 opacity-100' : 'w-0 opacity-0'}`} />
              <div className="w-2 h-2 bg-accent rotate-45" />
              <div className={`h-px bg-accent transition-all duration-1000 delay-300 ${isVisible ? 'w-16 opacity-100' : 'w-0 opacity-0'}`} />
            </div>
          </div>

          {/* Refined Tagline - Highly Readable */}
          <div className={`mb-16 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <p className="text-2xl md:text-3xl lg:text-4xl text-white font-light leading-relaxed tracking-wide max-w-4xl mx-auto">
              Trusted Excellence in Legal Representation
            </p>
            <p className="text-lg md:text-xl text-white/80 font-light mt-6 tracking-wide max-w-3xl mx-auto leading-relaxed">
              Protecting Your Rights with Integrity, Experience, and Unwavering Dedication
            </p>
          </div>

          {/* Premium CTA Button */}
          <div className={`transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <Link
              href="/contact"
              className="group inline-flex items-center px-14 py-5 bg-accent text-primary font-semibold text-lg tracking-widest uppercase rounded-sm hover:bg-accent/90 transition-all duration-300 shadow-2xl hover:shadow-accent/50 hover:scale-105"
            >
              <span>Schedule Consultation</span>
              <svg 
                className="ml-3 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>

          {/* Subtle trust indicators */}
          <div className={`mt-20 transition-all duration-1000 delay-900 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
            <div className="flex flex-wrap items-center justify-center gap-8 text-white/60 text-sm tracking-widest uppercase">
              <span className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Licensed & Certified</span>
              </span>
              <span className="hidden md:block text-accent">•</span>
              <span className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <span>Award-Winning Firm</span>
              </span>
              <span className="hidden md:block text-accent">•</span>
              <span className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
                <span>24/7 Availability</span>
              </span>
            </div>
          </div>

        </div>
      </div>

      {/* Bottom fade effect */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-primary to-transparent" />
    </section>
  );
}